import { Connectable, connectable, Observer, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { DelegatedEvent, delegatedObserver, markup2element } from '../dom/dom';
import { ControlOptions, TLMDControl, UIControl } from './control';

/**@interface
A collection of cell properties.
@property index The index number of the cell
@property content The inner HTML of the cell
@property classes an array of class names to add to the cell
@value the (string) value to set on the ''data-value'' attribute, which can be used to map back to the control's JSON value
 */
export interface Cell {
	index: number,
	content: string,
	value: string,
	classes: string[]
}

export abstract class SetPicker<T> extends UIControl<T> {
	private static CELL_CONTAINER_SELECTOR = '.tlmd-cell-container.picker';
	protected cellContainer: HTMLElement;

	protected cellClickedObserver: Connectable<T>

	public constructor(options: ControlOptions, protected count: number) {
		super(options);
	}

	/**@abstract
	A mapper that needs to convert the delegated event, triggered by a click on one of the cells, to the (json) value that the clicked cell represents.
	@param delegatedEvent the delegated event, triggered by a click on one of the cells
	@return the (json) value that the clicked cell represents
	*/
	protected abstract mapCellClickEventToValue(delegatedEvent: DelegatedEvent): T;
	
	/**@abstract
	Returns the content of the grid cell with given index. This method should be overriden by implementing base classes
	@param cellIndex the index of the grid cell which's  content is being requested
	@return the markup, classes and value for given cell index
	 */
	protected abstract getCell(cellIndex: number): Cell;

	protected getCellContainer(): HTMLElement {
		return this.cellContainer;
	}
	
	public subscribeToCellClickedObserver(observer: Partial<Observer<T>>): void {
		this.cellClickedObserver.subscribe(observer);
	}
	
	public close(){
		this.getContainer().classList.add('closed');
	}
	public open(){
		this.getContainer().classList.remove('closed');
	}

	protected build() {
		this.cellContainer = this.getContainer().querySelector(SetPicker.CELL_CONTAINER_SELECTOR);
		if (this.cellContainer == undefined) {
			this.cellContainer = <HTMLElement>markup2element('<div class="tlmd-cell-container picker"></div>');
			this.getContainer().append(this.cellContainer);
		}

		let gridCell = markup2element('<div class="tlmd-cell"></div>');
		this.cellContainer.innerHTML = '';
		for (let cellIndex = 0; cellIndex < this.count; cellIndex++) {
			this.cellContainer.appendChild(gridCell.cloneNode());
		}
		
		//make the close button work
		this.addSubscription( 
			delegatedObserver(this.getContainer(), 'click', '.tlmd-button.tlmd-close')
			.subscribe({ 
				next:  dEv =>{  
					dEv.event.preventDefault();
					 this.close();
					  } 
				}));

		//create an multicast observervable (a Subject)
		this.cellClickedObserver = connectable(
			delegatedObserver(this.getCellContainer(), 'click', 'div.tlmd-cell')
				.pipe(
					filter(delegatedEvent => !delegatedEvent.target.classList.contains('empty') && !delegatedEvent.target.classList.contains('disabled')),
					tap(delegatedEvent => {
						for (let cellIndex = 0; cellIndex < this.count; cellIndex++) {
							let cell = <HTMLElement>this.getCellContainer().children.item(cellIndex);
							cell.classList.remove('selected');
						}
						delegatedEvent.target.classList.add('selected');
					}),
					map(delegatedEvent => this.mapCellClickEventToValue(delegatedEvent)),
					tap(value => this.setValue(value))
				),
			{ connector: () => new Subject<T>(), resetOnDisconnect: false }
		);

		this.addSubscription(this.cellClickedObserver.connect());
	}


	private resetCellClasses(cellElem: HTMLElement): void {
		cellElem.className = 'tlmd-cell';
	}

	protected updateCells(value?: string): void {
		//iterate over all cells
		for (let cellIndex = 0; cellIndex < this.count; cellIndex++) {
			let cellElem = <HTMLElement>this.getCellContainer().children.item(cellIndex);

			this.resetCellClasses(cellElem);

			let cellContent = this.getCell(cellIndex);
			if (cellContent != undefined) {

				//apply the classes
				if (cellContent.classes != undefined) {
					cellElem.classList.add(...cellContent.classes);
				}

				if (cellContent.content != undefined) {
					//we have cell content
					cellElem.innerHTML = cellContent.content;

					if (cellContent.value != undefined) {
						//we have a cell value
						cellElem.dataset.value = cellContent.value;
						if (value !== undefined && cellContent.value === value) {
							//cell has current value
							cellElem.classList.add('selected');
						} else {
							//cell hasn't'got the  current value
						}
					} else {
						//cell has content but no value -> disabled
						cellElem.classList.add('disabled');
					}
				} else {
					//cell has no content;
					cellElem.innerHTML = '';
					cellElem.classList.add('empty');
				}
			} else {
				//cell is not defined;
				cellElem.innerHTML = '';
				cellElem.classList.add('empty');
			}
		}
	}

}

